import apiFetch from "@/app/auth/apiFetch";

const API_URL = import.meta.env.VITE_API_URL;

export interface FeatureFlag {
  id: number;
  key: string;
  description: string | null;
  enabled_by_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionOverride {
  subscription_id: number;
  user_id: string | null;
  email: string | null;
  enabled: boolean;
}

export interface FeatureFlagWithOverrides extends FeatureFlag {
  overrides: SubscriptionOverride[];
}

export async function listFeatureFlags(navigate): Promise<FeatureFlag[]> {
  const res = await apiFetch(`${API_URL}/admin/feature-flags`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement des feature flags");
  return res.json();
}

export async function getFeatureFlag(
  id: number,
  navigate,
): Promise<FeatureFlagWithOverrides> {
  const res = await apiFetch(
    `${API_URL}/admin/feature-flags/${id}`,
    { method: "GET" },
    navigate,
  );
  if (!res?.ok) throw new Error("Erreur lors du chargement du feature flag");
  return res.json();
}

export async function createFeatureFlag(
  payload: { key: string; description?: string; enabled_by_default: boolean },
  navigate,
): Promise<FeatureFlag> {
  const res = await apiFetch(
    `${API_URL}/admin/feature-flags`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    navigate,
  );
  if (!res?.ok) {
    const detail = await res?.json().catch(() => null);
    throw new Error(detail?.detail || "Echec de la creation");
  }
  return res.json();
}

export async function updateFeatureFlag(
  id: number,
  payload: { description?: string; enabled_by_default?: boolean },
  navigate,
): Promise<FeatureFlag> {
  const res = await apiFetch(
    `${API_URL}/admin/feature-flags/${id}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    navigate,
  );
  if (!res?.ok) throw new Error("Echec de la mise a jour");
  return res.json();
}

export async function deleteFeatureFlag(id: number, navigate): Promise<void> {
  const res = await apiFetch(
    `${API_URL}/admin/feature-flags/${id}`,
    { method: "DELETE" },
    navigate,
  );
  if (!res?.ok && res?.status !== 204) throw new Error("Echec de la suppression");
}

export async function setSubscriptionOverride(
  flagId: number,
  subscriptionId: number,
  enabled: boolean,
  navigate,
): Promise<SubscriptionOverride> {
  const res = await apiFetch(
    `${API_URL}/admin/feature-flags/${flagId}/subscriptions/${subscriptionId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled }),
    },
    navigate,
  );
  if (!res?.ok) throw new Error("Echec de la mise a jour de l'override");
  return res.json();
}

export async function deleteSubscriptionOverride(
  flagId: number,
  subscriptionId: number,
  navigate,
): Promise<void> {
  const res = await apiFetch(
    `${API_URL}/admin/feature-flags/${flagId}/subscriptions/${subscriptionId}`,
    { method: "DELETE" },
    navigate,
  );
  if (!res?.ok && res?.status !== 204) throw new Error("Echec de la suppression de l'override");
}
